import { useMemo } from 'react';
import { Merchant, Processor } from '@frontend/api-payments';
import { PaymentsFeatureFlags } from '@frontend/payments-hooks';
import { useAppFlagStore } from '@frontend/shared';
import { ActiveGateway } from '../types/context.types';
import { useLocationData } from './use-processor-data/use-location-data';
import { useLocationMerchant } from './use-processor-data/use-location-merchant';

export type UseProcessorDataProps = {
  locationId: string;
  isPublic: boolean;
  merchant?: Merchant;
};
export const useProcessorData = ({ locationId, isPublic, merchant: merchantProps }: UseProcessorDataProps) => {
  /**
   * TODO: Potentially need public endpoint equivalent
   */
  const { getFeatureFlagValue } = useAppFlagStore();

  const hasJustifiOnboardingEnabled = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableJustifyOnboarding,
    locationIds: [locationId],
  });

  const enableAchOnFileStripe = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.ACHOnFile,
    locationIds: [locationId],
  });

  const enableShareInEmail = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.ShareInEmail,
    locationIds: [locationId],
  });

  const enableAddAchMethodStripe = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableAddACH,
    locationIds: !!locationId ? [locationId] : [],
  });

  /**
   * TODO: Potentially need public endpoint equivalent
   */
  const { data: merchantQuery, isFetched: merchantIsFetched } = useLocationMerchant({ locationId });

  /**
   * In multi-location scenarios, the tranditional useMerchants() might not 100% match with the locationId given.
   * So, use merchant as a fallback to help with loading times, but don't disable the useLocationMerchant() query (above).
   */
  const merchant = useMemo(() => {
    return merchantQuery || merchantProps;
  }, [merchantProps, merchantQuery]);

  const activeProcessor = merchant?.processors?.find((processor: Processor) => processor.isActive === true);
  const stripeId = activeProcessor?.stripeId;
  const hasStripeId = !!stripeId;
  const hasStripeOnboarded = !!activeProcessor?.stripeOnboarded;
  const stripeCreatedNotOnboarded = hasStripeId && !hasStripeOnboarded;
  const hasStripePayments = hasStripeId && hasStripeOnboarded;

  const { terminalLocation: stripeLocationId } = merchant || {};

  const activeGateway: ActiveGateway = useMemo(() => {
    if (hasJustifiOnboardingEnabled && activeProcessor?.processorType?.toLowerCase() === 'justifi') {
      return 'justifi';
    }

    if (hasStripePayments) {
      return 'stripe';
    }

    return null;
  }, [hasStripePayments, activeProcessor, hasJustifiOnboardingEnabled]);

  const { data: locationData, isLoading: isLoadingLocationData } = useLocationData({ locationId, isPublic });

  /**
   * For now, justifi will not support ACH.
   * Hard coding.
   */
  const enableAchOnFileJustifi = false;

  return {
    locationData,
    isLoadingLocationData,
    activeGateway,
    merchant,
    activeProcessor,
    merchantIsFetched,
    stripeData: {
      stripeLocationId,
      enableAchOnFile: enableAchOnFileStripe,
      enableShareInEmail,
      stripeCreatedNotOnboarded,
      enableAddAchMethod: enableAddAchMethodStripe,
    },
    justifiData: {
      enableAchOnFile: enableAchOnFileJustifi,
      hasOnboardingEnabled: hasJustifiOnboardingEnabled,
      enableShareInEmail,
    },
  };
};

export type UseProcessorData = ReturnType<typeof useProcessorData>;
